import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Checkbox, Button, Drawer,
  List, ListItem, ListItemText, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, Typography, Box, IconButton, Tooltip, Tabs, Tab, Chip, Divider, Alert, Snackbar
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ClearIcon from "@mui/icons-material/Clear";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  getBallkids,
  getAttendanceForBallkid,
  saveAttendance,
  checkInBallkid,
  checkOutBallkid,
  getCheckInStatus,
  getCheckInStatusDetailed,
  getAttendanceComment,
  saveAttendanceComment,
  updateCheckInOutTimes,
  checkOutCrew,
  checkOutCourt,
  getAllCourtAssignments,
} from "../firebaseService";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";

const isAvailable = (val) => {
  if (!val) return false;
  if (val === true) return true;
  if (typeof val === "object" && val.available) return true;
  return false;
};

// Time options from 9:30 to 17:00 in 15-min steps
const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 9; h <= 17; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 9 && m < 30) continue;
      if (h === 17 && m > 0) continue;
      const val = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      opts.push(val);
    }
  }
  return opts;
})();

const getFromTime = (val) => {
  if (!val || val === true) return "";
  if (typeof val === "object" && val.from) return val.from;
  return "";
};

const formatTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const courts = ["Center Court", "Court 1", "Court 2", "Court 3", "Court 4"];

const SchedulePage = () => {
  const { daysOfWeek } = useTournamentConfig();

  const [schedule, setSchedule] = useState([]);
  const [editableSchedule, setEditableSchedule] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useDefaultTournamentDay(daysOfWeek);
  const [checkInStatus, setCheckInStatus] = useState({});
  const [detailedStatus, setDetailedStatus] = useState({});
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [timeEditCell, setTimeEditCell] = useState(null);
  const [drawerTab, setDrawerTab] = useState(0); // 0=CheckIn/Out, 1=Anwesend, 2=Crew/Platz
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Time edit dialog state
  const [timeEditDialog, setTimeEditDialog] = useState(null); // { ballkidId, name, checkIn, checkOut }
  const [editCheckIn, setEditCheckIn] = useState("");
  const [editCheckOut, setEditCheckOut] = useState("");

  // Court assignments for crew/court checkout
  const [courtAssignments, setCourtAssignments] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      const allBallkids = await getBallkids();
      const attendancePromises = allBallkids.map(async (bk) => {
        const attendance = await getAttendanceForBallkid(bk.id);
        return {
          id: bk.id,
          name: bk.name,
          availability: daysOfWeek.map((d) => attendance.availability?.[d] || false),
        };
      });

      const combinedData = await Promise.all(attendancePromises);
      const sortedData = combinedData.sort((a, b) => a.name.localeCompare(b.name));

      setSchedule(sortedData);
      setEditableSchedule([...sortedData]);

      const loadedComment = await getAttendanceComment();
      setComment(loadedComment);
    };

    fetchData();
  }, [daysOfWeek]);

  useEffect(() => {
    const fetchCheckInStatus = async () => {
      const [status, detailed, assignments] = await Promise.all([
        getCheckInStatus(selectedDay),
        getCheckInStatusDetailed(selectedDay),
        getAllCourtAssignments(selectedDay),
      ]);
      setCheckInStatus(status);
      setDetailedStatus(detailed);
      setCourtAssignments(assignments);
    };
    if (drawerOpen) fetchCheckInStatus();
  }, [drawerOpen, selectedDay]);

  const refreshCheckInStatus = async () => {
    const [status, detailed] = await Promise.all([
      getCheckInStatus(selectedDay),
      getCheckInStatusDetailed(selectedDay),
    ]);
    setCheckInStatus(status);
    setDetailedStatus(detailed);
  };

  const handleCheckboxChange = (ballkidId, dayIndex) => {
    setEditableSchedule((prev) =>
      prev.map((bk) => {
        if (bk.id !== ballkidId) return bk;
        const current = bk.availability[dayIndex];
        const currentlyAvailable = isAvailable(current);
        const newAvailability = [...bk.availability];
        newAvailability[dayIndex] = currentlyAvailable ? false : true;
        return { ...bk, availability: newAvailability };
      })
    );
  };

  const handleTimeChange = (ballkidId, dayIndex, time) => {
    setEditableSchedule((prev) =>
      prev.map((bk) => {
        if (bk.id !== ballkidId) return bk;
        const newAvailability = [...bk.availability];
        if (time) {
          newAvailability[dayIndex] = { available: true, from: time };
        } else {
          newAvailability[dayIndex] = true;
        }
        return { ...bk, availability: newAvailability };
      })
    );
  };

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
    if (!isEditing) {
      setEditableSchedule([...schedule]);
    }
    setTimeEditCell(null);
  };

  const handleSave = async () => {
    setSchedule([...editableSchedule]);
    await Promise.all(
      editableSchedule.map(async (editedBk) => {
        const availabilityMap = daysOfWeek.reduce((acc, d, index) => {
          acc[d] = editedBk.availability[index];
          return acc;
        }, {});
        await saveAttendance(editedBk.id, availabilityMap);
      })
    );
    setIsEditing(false);
    setTimeEditCell(null);
  };

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const handleCheckIn = async (ballkidId) => {
    await checkInBallkid(ballkidId, selectedDay);
    setCheckInStatus((prev) => ({ ...prev, [ballkidId]: true }));
    setDetailedStatus((prev) => ({
      ...prev,
      [ballkidId]: { checkedIn: true, checkedOut: false, checkIn: new Date().toISOString(), checkOut: null },
    }));
  };

  const handleCheckOut = async (ballkidId) => {
    await checkOutBallkid(ballkidId, selectedDay);
    setCheckInStatus((prev) => ({ ...prev, [ballkidId]: "checkedOut" }));
    setDetailedStatus((prev) => ({
      ...prev,
      [ballkidId]: { ...prev[ballkidId], checkedOut: true, checkOut: new Date().toISOString() },
    }));
  };

  const handleCheckOutAll = async () => {
    const checkedInBallkids = schedule
      .filter(bk => isAvailable(bk.availability[daysOfWeek.indexOf(selectedDay)]))
      .filter(bk => checkInStatus[bk.id] === true);

    await Promise.all(checkedInBallkids.map(bk => checkOutBallkid(bk.id, selectedDay)));
    setCheckInStatus((prev) => {
      const updated = { ...prev };
      checkedInBallkids.forEach(bk => { updated[bk.id] = "checkedOut"; });
      return updated;
    });
    await refreshCheckInStatus();
  };

  // Manual time override
  const handleOpenTimeEdit = (ballkidId, name) => {
    const detail = detailedStatus[ballkidId];
    const checkInTime = detail?.checkIn ? new Date(detail.checkIn).toTimeString().slice(0, 5) : "";
    const checkOutTime = detail?.checkOut ? new Date(detail.checkOut).toTimeString().slice(0, 5) : "";
    setEditCheckIn(checkInTime);
    setEditCheckOut(checkOutTime);
    setTimeEditDialog({ ballkidId, name });
  };

  const handleSaveTimeEdit = async () => {
    if (!timeEditDialog) return;
    const { ballkidId } = timeEditDialog;

    const today = new Date();
    const dateStr = today.toISOString().split("T")[0];

    const newCheckIn = editCheckIn ? new Date(`${dateStr}T${editCheckIn}:00`).toISOString() : null;
    const newCheckOut = editCheckOut ? new Date(`${dateStr}T${editCheckOut}:00`).toISOString() : null;

    await updateCheckInOutTimes(ballkidId, selectedDay, newCheckIn, newCheckOut);
    await refreshCheckInStatus();

    // Update local status
    if (newCheckIn && !newCheckOut) {
      setCheckInStatus((prev) => ({ ...prev, [ballkidId]: true }));
    } else if (newCheckIn && newCheckOut) {
      setCheckInStatus((prev) => ({ ...prev, [ballkidId]: "checkedOut" }));
    }

    setSnackbar({ open: true, message: "Zeiten aktualisiert", severity: "success" });
    setTimeEditDialog(null);
  };

  // Crew/Court checkout
  const handleCheckOutCrew = async (court, crew) => {
    const checkedOut = await checkOutCrew(selectedDay, court, crew);
    await refreshCheckInStatus();
    setSnackbar({
      open: true,
      message: `${checkedOut.length} Ballkinder von ${court} ${crew} ausgecheckt`,
      severity: "success",
    });
  };

  const handleCheckOutCourtAll = async (court) => {
    const checkedOut = await checkOutCourt(selectedDay, court);
    await refreshCheckInStatus();
    setSnackbar({
      open: true,
      message: `${checkedOut.length} Ballkinder von ${court} ausgecheckt`,
      severity: "success",
    });
  };

  const handleOpenCommentDialog = () => setCommentDialogOpen(true);
  const handleCloseCommentDialog = async () => {
    await saveAttendanceComment(comment);
    setCommentDialogOpen(false);
  };

  // Helper: currently present ballkids (memoized)
  const presentBallkids = useMemo(() =>
    schedule.filter((bk) => {
      const detail = detailedStatus[bk.id];
      return detail?.checkedIn && !detail?.checkedOut;
    }),
    [schedule, detailedStatus]
  );

  // Helper: available ballkids for selected day (memoized)
  const availableBallkids = useMemo(() =>
    schedule
      .filter((bk) => isAvailable(bk.availability[daysOfWeek.indexOf(selectedDay)]))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [schedule, selectedDay, daysOfWeek]
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ textAlign: "center", mb: 2 }}>
        Ballkinder Anwesenheitsplan
      </Typography>
      <Button variant="outlined" onClick={handleOpenCommentDialog} style={{ marginBottom: 20 }}>
        Kommentar hinzufuegen
      </Button>
      <Button variant="contained" color={isEditing ? "secondary" : "primary"} onClick={handleEditToggle} style={{ marginBottom: 20, marginLeft: 10 }}>
        {isEditing ? "Bearbeitung abbrechen" : "Bearbeitung starten"}
      </Button>
      {isEditing && (
        <Button variant="contained" color="success" onClick={handleSave} style={{ marginBottom: 20, marginLeft: 10 }}>
          Aenderungen speichern
        </Button>
      )}
      <Button variant="contained" color="info" onClick={toggleDrawer(true)} style={{ float: "right", marginBottom: 20 }}>
        Zu CheckINs/OUTs
      </Button>

      {/* Enhanced Check-In/Out Drawer */}
      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 420, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Check-In/Out</Typography>
          <Select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)} fullWidth sx={{ mb: 1 }}>
            {daysOfWeek.map((d) => (
              <MenuItem key={d} value={d}>{d}</MenuItem>
            ))}
          </Select>

          <Tabs value={drawerTab} onChange={(e, v) => setDrawerTab(v)} sx={{ mb: 1 }}>
            <Tab label="Check-In/Out" />
            <Tab label={`Anwesend (${presentBallkids.length})`} />
            <Tab label="Crew/Platz" />
          </Tabs>

          {/* Tab 0: Normal Check-In/Out */}
          {drawerTab === 0 && (
            <>
              <Button
                variant="contained"
                color="warning"
                onClick={handleCheckOutAll}
                fullWidth
                sx={{ mb: 1 }}
                disabled={!availableBallkids.some(bk => checkInStatus[bk.id] === true)}
              >
                Alle auschecken
              </Button>
              <List dense>
                {availableBallkids.map((ballkid) => {
                    const dayIdx = daysOfWeek.indexOf(selectedDay);
                    const fromTime = getFromTime(ballkid.availability[dayIdx]);
                    const detail = detailedStatus[ballkid.id];
                    const checkInTime = detail?.checkIn ? formatTime(detail.checkIn) : null;
                    const checkOutTime = detail?.checkOut ? formatTime(detail.checkOut) : null;

                    return (
                      <ListItem key={ballkid.id} sx={{ pr: 1 }}>
                        <ListItemText
                          primary={ballkid.name}
                          secondary={
                            <>
                              {fromTime && <span>ab {fromTime}</span>}
                              {checkInTime && (
                                <span style={{ marginLeft: fromTime ? 8 : 0 }}>
                                  {checkInTime}{checkOutTime ? ` - ${checkOutTime}` : ""}
                                </span>
                              )}
                            </>
                          }
                        />
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                          {(detail?.checkedIn) && (
                            <Tooltip title="Zeiten bearbeiten">
                              <IconButton size="small" onClick={() => handleOpenTimeEdit(ballkid.id, ballkid.name)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {checkInStatus[ballkid.id] === "checkedOut" || (detail?.checkedIn && detail?.checkedOut) ? (
                            <Button variant="contained" disabled size="small" sx={{ minWidth: 90 }}>CHECKED OUT</Button>
                          ) : checkInStatus[ballkid.id] || (detail?.checkedIn && !detail?.checkedOut) ? (
                            <Button variant="contained" color="error" size="small" sx={{ minWidth: 90 }} onClick={() => handleCheckOut(ballkid.id)}>Check-Out</Button>
                          ) : (
                            <Button variant="contained" color="success" size="small" sx={{ minWidth: 90 }} onClick={() => handleCheckIn(ballkid.id)}>Check-In</Button>
                          )}
                        </Box>
                      </ListItem>
                    );
                  })}
              </List>
            </>
          )}

          {/* Tab 1: Anwesend (Currently Present) */}
          {drawerTab === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 1 }}>
                <strong>{presentBallkids.length}</strong> Ballkinder gerade anwesend
              </Alert>
              <List dense>
                {presentBallkids
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((ballkid) => {
                    const detail = detailedStatus[ballkid.id];
                    const checkInTime = detail?.checkIn ? formatTime(detail.checkIn) : "";
                    return (
                      <ListItem key={ballkid.id}>
                        <ListItemText
                          primary={ballkid.name}
                          secondary={`Eingecheckt seit ${checkInTime}`}
                        />
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                          <Tooltip title="Zeiten bearbeiten">
                            <IconButton size="small" onClick={() => handleOpenTimeEdit(ballkid.id, ballkid.name)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Button variant="contained" color="error" size="small" onClick={() => handleCheckOut(ballkid.id)}>
                            Check-Out
                          </Button>
                        </Box>
                      </ListItem>
                    );
                  })}
                {presentBallkids.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                    Keine Ballkinder aktuell eingecheckt.
                  </Typography>
                )}
              </List>
            </Box>
          )}

          {/* Tab 2: Crew/Platz Checkout */}
          {drawerTab === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Checke ganze Crews oder alle Ballkinder eines Platzes auf einmal aus.
              </Typography>
              {courts.map((court) => {
                const crewKeys = ["crewA", "crewB", "crewC", "crewD"];
                const hasAssignments = crewKeys.some((crew) =>
                  courtAssignments[`${selectedDay}_${court}_${crew}`]?.length > 0
                );
                if (!hasAssignments) return null;

                return (
                  <Paper key={court} sx={{ p: 2, mb: 2 }} variant="outlined">
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{court}</Typography>
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        startIcon={<GroupsIcon />}
                        onClick={() => handleCheckOutCourtAll(court)}
                      >
                        Platz auschecken
                      </Button>
                    </Box>
                    {crewKeys.map((crew) => {
                      const members = courtAssignments[`${selectedDay}_${court}_${crew}`] || [];
                      if (members.length === 0) return null;
                      return (
                        <Box key={crew} sx={{ mb: 1 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {crew.replace("crew", "Crew ")} ({members.length})
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              color="warning"
                              onClick={() => handleCheckOutCrew(court, crew)}
                            >
                              Crew auschecken
                            </Button>
                          </Box>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                            {members.map((name, i) => (
                              <Chip key={i} label={name} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      );
                    })}
                  </Paper>
                );
              })}
              {!courts.some((court) =>
                ["crewA", "crewB", "crewC"].some((crew) =>
                  courtAssignments[`${selectedDay}_${court}_${crew}`]?.length > 0
                )
              ) && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                  Keine Platzeinteilungen fuer diesen Tag vorhanden.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Time Edit Dialog */}
      <Dialog open={!!timeEditDialog} onClose={() => setTimeEditDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Zeiten bearbeiten: {timeEditDialog?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Manuelle Korrektur der Check-In/Out Zeiten fuer {selectedDay}.
          </Typography>
          <TextField
            label="Check-In Zeit"
            select
            value={editCheckIn}
            onChange={(e) => setEditCheckIn(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="">-- keine --</MenuItem>
            {TIME_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField
            label="Check-Out Zeit"
            select
            value={editCheckOut}
            onChange={(e) => setEditCheckOut(e.target.value)}
            fullWidth
          >
            <MenuItem value="">-- keine --</MenuItem>
            {TIME_OPTIONS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeEditDialog(null)}>Abbrechen</Button>
          <Button onClick={handleSaveTimeEdit} variant="contained" color="primary">Speichern</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={commentDialogOpen} onClose={handleCloseCommentDialog} fullWidth maxWidth="md">
        <DialogTitle>Kommentar hinzufuegen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField autoFocus margin="dense" label="Kommentar" type="text" fullWidth multiline rows={8} variant="outlined" value={comment} onChange={(e) => setComment(e.target.value)} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)} color="primary">Abbrechen</Button>
          <Button onClick={handleCloseCommentDialog} color="primary">Speichern</Button>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper} sx={{ flexGrow: 1, overflow: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ position: "sticky", top: 0, left: 0, zIndex: 3, backgroundColor: "#1a2138" }}>Name</TableCell>
              {daysOfWeek.map((d, index) => (
                <TableCell key={index} align="center" sx={{ position: "sticky", top: 0, backgroundColor: "#1a2138", zIndex: 1 }}>
                  {d}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {editableSchedule.map((ballkid) => (
              <TableRow key={ballkid.id}>
                <TableCell sx={{ position: "sticky", left: 0, backgroundColor: "#1a2138", zIndex: 2 }}>
                  {ballkid.name}
                </TableCell>
                {ballkid.availability.map((val, index) => {
                  const available = isAvailable(val);
                  const fromTime = getFromTime(val);
                  const isTimeEditing = timeEditCell?.ballkidId === ballkid.id && timeEditCell?.dayIndex === index;

                  return (
                    <TableCell key={index} align="center" sx={{ minWidth: 100 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
                        <Checkbox
                          checked={available}
                          onChange={() => handleCheckboxChange(ballkid.id, index)}
                          disabled={!isEditing}
                          size="small"
                        />
                        {available && isEditing && (
                          isTimeEditing ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                              <Select
                                value={fromTime}
                                onChange={(e) => handleTimeChange(ballkid.id, index, e.target.value)}
                                size="small"
                                displayEmpty
                                sx={{ width: 100, fontSize: "0.75rem", "& .MuiSelect-select": { py: 0.5 } }}
                              >
                                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>-- Zeit --</MenuItem>
                                {TIME_OPTIONS.map((t) => <MenuItem key={t} value={t} sx={{ fontSize: "0.75rem" }}>{t}</MenuItem>)}
                              </Select>
                              {fromTime && (
                                <IconButton size="small" onClick={() => handleTimeChange(ballkid.id, index, "")}>
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          ) : (
                            <Tooltip title="Zeit eingeben (ab wann verfuegbar)">
                              <Button
                                size="small"
                                sx={{ fontSize: "0.65rem", minWidth: 0, py: 0, px: 0.5, textTransform: "none" }}
                                onClick={() => setTimeEditCell({ ballkidId: ballkid.id, dayIndex: index })}
                                startIcon={fromTime ? null : <AccessTimeIcon sx={{ fontSize: "0.8rem" }} />}
                              >
                                {fromTime ? `ab ${fromTime}` : ""}
                              </Button>
                            </Tooltip>
                          )
                        )}
                        {available && !isEditing && fromTime && (
                          <Typography variant="caption" sx={{ fontSize: "0.65rem", color: "text.secondary" }}>
                            ab {fromTime}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SchedulePage;
